import { isUndefined } from 'lodash-es';

import { CONFIGURATOR_DEFAULT_LOCALE } from '../constants';
import {
  ConfiguratorTrafficHighlight,
  ConfiguratorTrafficPlan,
  TextTranslations,
  TrafficOption,
  TrafficOptionHighlight,
  TrafficOptionRegion,
} from '../types';

/**
 * Resolve a localized string. Tries the exact locale, then its base language (so `es-MX`
 * matches an `es` key), then the default locale, then the first available value.
 */
function pickText(text: TextTranslations | string | undefined, locale: string): string {
  if (typeof text === 'string') return text;
  if (!text) return '';
  const base = locale.split('-')[0];
  return (
    text[locale] ?? text[base] ?? text[CONFIGURATOR_DEFAULT_LOCALE] ?? Object.values(text)[0] ?? ''
  );
}

/** Stable plan id for a traffic option: traffic_option_data.id → key → numeric option id. */
export function getTrafficPlanId(option: TrafficOption): string {
  const data = option.traffic_option_data ?? {};
  return data.id ?? data.key ?? String(option.id);
}

/**
 * The region-specific content for an option, or undefined when the option isn't offered
 * in that region (i.e. `traffic_option_data.regions[regionCode]` is absent).
 */
export function getTrafficOptionRegion(
  option: TrafficOption | undefined,
  regionCode: string | undefined,
): TrafficOptionRegion | undefined {
  if (isUndefined(option) || isUndefined(regionCode)) {
    return undefined;
  }
  return option.traffic_option_data?.regions?.[regionCode];
}

/**
 * Build the selectable traffic plans for a region: active options that have a
 * `regions[regionCode]` entry. The plan name comes from the region (falling back to the
 * shared option name). Returns [] when no region is selected.
 */
export function buildTrafficPlans(
  options: TrafficOption[],
  regionCode: string | undefined,
  locale: string = CONFIGURATOR_DEFAULT_LOCALE,
): ConfiguratorTrafficPlan[] {
  if (isUndefined(regionCode)) {
    return [];
  }

  return options.reduce<ConfiguratorTrafficPlan[]>((acc, option) => {
    if (option.is_active === false) {
      return acc;
    }
    const region = getTrafficOptionRegion(option, regionCode);
    if (isUndefined(region)) {
      return acc;
    }
    const data = option.traffic_option_data ?? {};
    acc.push({
      id: getTrafficPlanId(option),
      // Resolve name/description to the market locale here — the template would otherwise
      // resolve the raw localized objects with the UI language (e.g. always "en").
      name: pickText(region.name ?? data.name ?? option.name, locale),
      description: pickText(data.description ?? option.description, locale),
      is_active: option.is_active ?? true,
      art_bundle: data.art_bundle ?? option.art_bundle ?? [],
    });
    return acc;
  }, []);
}

/** Map a region's localized highlights into the UI highlight shape (resolved strings). */
export function mapTrafficHighlights(
  highlights: TrafficOptionHighlight[] | undefined,
  locale: string = CONFIGURATOR_DEFAULT_LOCALE,
): ConfiguratorTrafficHighlight[] {
  if (!highlights?.length) {
    return [];
  }

  return highlights.map((highlight) => ({
    title: pickText(highlight.title, locale),
    description: pickText(highlight.description, locale),
    groups: (highlight.groups ?? []).map((group) => {
      const moreCount = group.more_count ?? 0;
      const moreLabel = moreCount > 0 ? pickText(group.more_label, locale).replace('{count}', String(moreCount)) : '';
      return {
        title: pickText(group.title, locale),
        items: (group.items ?? []).map((item) => ({
          name: pickText(item.name, locale),
          logo: item.logo,
        })),
        moreCount,
        moreLabel,
      };
    }),
  }));
}
